import { useContext, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { trpc, type RouterOutputs } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { chunk } from "./chunk";

type Classification = RouterOutputs["leads"]["classifyRecipients"]["results"][number];

type Props = {
  /** What the merchant uploaded: a phone, a BSUID, or a WhatsApp username. */
  terms: string[];
  whatsappConnectionId: string;
  includeOtherEmployee: boolean;
  includeAi: boolean;
  onToggleOtherEmployee: (value: boolean) => void;
  onToggleAi: (value: boolean) => void;
  // null while classification is loading/unknown; a number once resolved
  onEffectiveCountChange?: (count: number | null) => void;
};

const ExpandableList = ({ items }: { items: Classification[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const showEmployee = items.some((item) => item.employeeName);

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex min-h-11 items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
      >
        {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        {isExpanded ? "Ocultar lista" : "Ver lista"}
      </button>
      {isExpanded && (
        <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white">
          <div
            className={cn(
              "sticky top-0 grid gap-2 border-b border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-400",
              showEmployee ? "grid-cols-3" : "grid-cols-2"
            )}
          >
            <span>Nombre</span>
            <span>Contacto</span>
            {showEmployee && <span>Asesor</span>}
          </div>
          {items.map((item) => (
            <div
              key={item.term}
              className={cn(
                "grid items-center gap-2 border-b border-gray-100 px-3 py-1.5 text-xs last:border-b-0",
                showEmployee ? "grid-cols-3" : "grid-cols-2"
              )}
            >
              <span className="truncate">{item.leadName || "Sin nombre"}</span>
              <span className="truncate text-gray-500">{item.term}</span>
              {showEmployee && (
                <span className="truncate font-medium text-gray-600">
                  {item.employeeName || "—"}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RecipientReviewPanel = ({
  terms,
  whatsappConnectionId,
  includeOtherEmployee,
  includeAi,
  onToggleOtherEmployee,
  onToggleAi,
  onEffectiveCountChange,
}: Props) => {
  const { merchantId } = useContext(MerchantContext);

  const chunks = useMemo(() => chunk([...new Set(terms)]), [terms]);
  const queries = trpc.useQueries((t) =>
    chunks.map((chunkedTerms) =>
      t.leads.classifyRecipients({
        merchantId: merchantId || "",
        whatsappConnectionId,
        terms: chunkedTerms,
      })
    )
  );
  const isLoading = queries.some((q) => q.isLoading);
  const results = queries.flatMap((q) => q.data?.results ?? []);

  const groups = {
    newOnes: results.filter((r) => r.status === "new"),
    mine: results.filter((r) => r.status === "mine"),
    unassigned: results.filter((r) => r.status === "unassigned"),
    otherEmployee: results.filter((r) => r.status === "otherEmployee"),
    ai: results.filter((r) => r.status === "ai"),
    unreachable: results.filter((r) => r.status === "unreachable"),
  };

  // Unreachable rows are deliberately absent: unlike the two toggled groups,
  // they are not a choice the merchant can make. There is no address to send to.
  const effectiveCount =
    groups.newOnes.length +
    groups.mine.length +
    groups.unassigned.length +
    (includeOtherEmployee ? groups.otherEmployee.length : 0) +
    (includeAi ? groups.ai.length : 0);

  useEffect(() => {
    onEffectiveCountChange?.(isLoading ? null : effectiveCount);
  }, [isLoading, effectiveCount]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!terms.length || !whatsappConnectionId) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <p className="text-sm font-bold">Revisión de destinatarios</p>

        {isLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
            <Spinner size="sm" />
            Verificando destinatarios en esta línea...
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="mt-0.5 size-4 shrink-0 text-green-600" />
              <p className="text-sm text-gray-700">
                <span className="font-bold">{groups.newOnes.length}</span> nuevos — se creará una
                conversación
              </p>
            </div>

            {groups.mine.length > 0 && (
              <div className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 size-4 shrink-0 text-green-600" />
                <p className="text-sm text-gray-700">
                  <span className="font-bold">{groups.mine.length}</span> en conversaciones que
                  atiendes — se enviará en su conversación existente
                </p>
              </div>
            )}

            {groups.unassigned.length > 0 && (
              <div className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 size-4 shrink-0 text-green-600" />
                <p className="text-sm text-gray-700">
                  <span className="font-bold">{groups.unassigned.length}</span> sin asesor asignado —
                  se enviará en su conversación existente y será asignado a ti
                </p>
              </div>
            )}

            {groups.otherEmployee.length > 0 && (
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-yellow-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p
                      className={cn(
                        "text-sm",
                        includeOtherEmployee ? "text-gray-700" : "text-gray-500"
                      )}
                    >
                      <span className="font-bold">{groups.otherEmployee.length}</span> asignados a
                      otros asesores
                    </p>
                    <label className="flex shrink-0 items-center gap-2 text-xs font-medium text-gray-600">
                      Incluir
                      <Switch
                        checked={includeOtherEmployee}
                        onCheckedChange={onToggleOtherEmployee}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {includeOtherEmployee
                      ? "Su conversación actual se cerrará y se creará una nueva."
                      : "Serán omitidos del envío."}
                  </p>
                  <ExpandableList items={groups.otherEmployee} />
                </div>
              </div>
            )}

            {groups.ai.length > 0 && (
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-yellow-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className={cn("text-sm", includeAi ? "text-gray-700" : "text-gray-500")}>
                      <span className="font-bold">{groups.ai.length}</span> tienen una conversación
                      activa con el agente IA
                    </p>
                    <label className="flex shrink-0 items-center gap-2 text-xs font-medium text-gray-600">
                      Incluir
                      <Switch checked={includeAi} onCheckedChange={onToggleAi} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {includeAi
                      ? "Tomarás el control de esas conversaciones y la IA se desactivará."
                      : "Serán omitidos del envío."}
                  </p>
                  <ExpandableList items={groups.ai} />
                </div>
              </div>
            )}

            {groups.unreachable.length > 0 && (
              <div className="flex items-start gap-2">
                <XCircle className="mt-0.5 size-4 shrink-0 text-red-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-700">
                    <span className="font-bold">{groups.unreachable.length}</span> no encontrados —
                    no coinciden con ningún lead de esta línea
                  </p>
                  {/* No toggle: WhatsApp only accepts un teléfono o un ID, so a
                      username we have never seen has no address to send to. */}
                  <p className="text-xs text-gray-500">
                    Un usuario de WhatsApp solo sirve para buscar un lead que ya existe. Si nunca
                    nos ha escrito, necesitas su teléfono o su ID de WhatsApp.
                  </p>
                  <ExpandableList items={groups.unreachable} />
                </div>
              </div>
            )}

            {effectiveCount === 0 && (
              <p className="text-sm font-medium text-red-600">
                No has seleccionado ningún destinatario para el envío.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipientReviewPanel;
